import * as merge from 'deepmerge'

import {
  getCSSVariable,
  formatYear
} from '../../../shared/utils'

import {
  getFormatterForVarName,
} from '../app/selectors'
import { getLang } from '../app/selectors/lang'
import { getDataSubset } from '../../timeseries/utils'
import { ticks } from 'd3-array'

/** Default options for timeseries container */
const gridOptions = {
  top: '24',
  right: '24',
  bottom: '24',
  left: '24'
}

/** Default options for x axis */
const xAxisOptions = {
  type: 'value',
  nameTextStyle: {
    fontSize: 14,
    fontWeight: 'bold'
  },
  nameLocation: 'center',
  nameGap: 16,
  splitLine: {
    show: false
  },
  axisLine: {
    show: false,
    onZero: 0
  },
  axisTick: {
    show: false,
  }
}

/** Default options for y axis */
const yAxisOptions = {
  type: 'value',
  splitLine: {
    show: false,
    lineStyle: {
      type: 'dashed',
      color: ['rgba(0,0,0,0.1)']
    }
  },
  axisTick: {
    show: false
  },
  axisLine: {
    show: false
  }
}

/** Default options for visual map */
const visualMapOptions = {
  dimension: 1,
  calculable: false,
  show: false
}

const formatYValue = (varName, val) => {
  const formatter = getFormatterForVarName(varName)
  return formatter(val)
}

/**
 * Merge axis overrides with default axis options
 * @param {string} axisName 'x' or 'y'
 * @param {object} overrides https://ecomfe.github.io/echarts-doc/public/en/option.html#xAxis
 */
const getAxisOptions = (axisName, overrides = {}) =>
  merge(
    axisName === 'x' ? xAxisOptions : yAxisOptions,
    overrides
  )

/**
 * Merge container overrides with default container options
 * @param {object} overrides https://ecomfe.github.io/echarts-doc/public/en/option.html#grid
 */
const getGridOptions = (overrides = {}) =>
  merge(gridOptions, overrides)

/**
 * Merge visual map overrides with default visual map options
 * @param {object} overrides https://ecomfe.github.io/echarts-doc/public/en/option.html#visualMap
 */
const getVisualMapOptions = (overrides = []) =>
  overrides.map(vm => merge(visualMapOptions, vm))

/**
 * Gets the base timeseries config with the provided overrides
 * @param {*} overrides any override options for the timeseries
 */
const extendTimeseriesStyle = (overrides = {}) => ({
  ...overrides,
  grid: getGridOptions(overrides.grid),
  xAxis: getAxisOptions('x', overrides.xAxis),
  yAxis: getAxisOptions('y', overrides.yAxis),
  visualMap: getVisualMapOptions(overrides.visualMap)
})

const isDataReady = ({ data }) => data && data.length !== 0

/**
 *
 * @param {*} value
 * @param {*} metric metric to get key for (e.g. 'avg', 'avg_gap', 'grd', etc.)
 */
const getLangKeyForAxisLabel = (value) => {
  return value
  // const midPoint = metric.toLowerCase() === 'grd' ? 1 : 0
  // const base = 'AXIS_' + metric.toUpperCase()
  // const position =
  //   value === midPoint
  //     ? '_MID'
  //     : value > midPoint
  //     ? '_HIGH'
  //     : '_LOW'
  // const single = value === 1 ? '_SINGLE' : ''
  // return base + position + single
}

var STEPS = 6

const NO_DIFF_SIGN = [
  'frl',
  'prm',
  'pef'
]

/** GRID CONFIGURATION  */

export const grid = () => {
  return {
    top: getCSSVariable('--sp-top'),
    // right: 0,
    // bottom: getCSSVariable('--sp-bottom'),
    bottom: 45,
    // left: getCSSVariable('--sp-left'),
    left: 40,
  }
}

/** TIMESERIES OVERLAY CONFIGURATION */

/**
 * Gets `series.markPoints` echart options based on an
 * array of points.
 * @param {array<{axis, x, y, label, options}>} points
 */
const getAxisLabels = points => {
  return {
    animation: false,
    silent: true,
    data: points
      .map(p => (p.axis ? getAxisLabel(p) : null))
      .filter(p => p)
  }
}

/**
 * Gets a point that falls on the x or y axis
 * @param {object} point
 */
const getAxisLabel = ({
  axis,
  x,
  y,
  label,
  labelStyle,
  options
}) => {
  const position =
    axis === 'y' ? { x: x, yAxis: y } : { y: y, xAxis: x }
  return {
    ...position,
    symbol: 'circle',
    symbolSize: 1,
    label: {
      formatter: '{val|' + label + '}',
      align: axis === 'y' ? 'right' : 'center',
      offset: axis === 'x' ? [0, 0] : [0, 0],
      rich: {
        val: {
          fontFamily:
            'maisonneue-book, lato, helvetica neue, Arial, sans-serif, -apple-system',
          fontSize: 8,
          fontWeight: 'normal',
          color: '#757575',
          borderWidth: 0,
          borderColor: 'rgba(0,0,0,0)',
          backgroundColor: '#fafafa',
          textBorderColor: 'transparent',
          textBorderWidth: 0,
          lineHeight: axis === 'y' ? 18 : 12
        }
      },
      ...labelStyle
    },
    ...options
  }
}

/**
 * Gets `series.markLine` echart options based on an
 * array of lines.
 * @param {array<{axis, position, style}>} lines
 */
const getAxisLines = lines => {
  return {
    animation: false,
    silent: true,
    data: lines
      .map(l => (l.axis ? getAxisLine(l) : null))
      .filter(l => l)
  }
}

/**
 * Gets line data that spans the graph on the x or y axis
 * @param {object} line
 */
const getAxisLine = ({
  axis = 'y',
  position = 0,
  lineStyle,
  start,
  end,
  midPoint = false
}) => {
  const startPosition =
    axis === 'y'
      ? { x: start || 0, yAxis: position }
      : { y: start || 0, xAxis: position }
  const endPosition =
    axis === 'y'
      ? { x: end || '100%', yAxis: position }
      : { y: end || '97%', xAxis: position }
  return [
    {
      ...startPosition,
      symbol: 'none',
      lineStyle: {
        color: midPoint ? '#888' : 'rgba(0,0,0,0.1)',
        type: 'solid',
        ...lineStyle
      }
    },
    {
      ...endPosition,
      symbol: 'none'
    }
  ]
}


/** X AXIS CONFIGURATION */

const getXAxis = ({ region, extent, varName, ...rest }) => {
  const [min, max] = extent
  return {
    min,
    max,
    interval: varName === "abs" ? 2 : 3,
    // inverse: region === 'schools',
    axisLabel: {
      // show: false
      // rotate: 30,
      margin: 8,
      fontSize: 10,
      formatter: formatYear
    },
    axisTick: {
      show: true,
      lineStyle: { type: 'solid', color: '#e4e4e4' }
    },
    splitLine: {
      show: true,
      interval: 1,
      lineStyle: { type: 'solid', color: '#e4e4e4' }
    },
    ...rest
  }
}

const xAxis = ({ varName, region, extent }) =>
  getXAxis({
    varName,
    region,
    extent
  })

/** Y AXIS CONFIGURATION */

const getYAxis = ({ varName, extent, ...rest }) => {
  const [min, max] = extent
  const formatter = value => formatYValue(varName, value)
  return {
    min,
    max,
    position: 'left',
    // offset: -5,
    axisLabel: {
      // rotate: 30,
      // margin: 4,
      fontSize: 10,
      formatter: formatter
    },
    axisTick: {
      show: true,
      lineStyle: { type: 'solid', color: '#e4e4e4' }
    },
    splitLine: {
      show: true,
      lineStyle: { type: 'solid', color: '#e4e4e4' }
    },
    nameLocation: 'middle',
    ...rest
  }
}

const yAxis = ({ varName, region, extent }) =>
  getYAxis({ varName, region, extent })

/**
 * Gets the echart options for the main chart, with visual map, dot sizing, grid lines, axis labels, etc.
 * @param {*} variant
 * @param {*} param1
 * @param {*} base
 * @returns
 */
export const getOverlayOptions = (
  { data = [], metricGroup, xVar, yVar, extents, colorExtent, region, xExtentsMissing },
  base
) => {

  const noDataImgSrc = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAYAAAAGCAYAAADgzO9IAAAACXBIWXMAAALFAAACxQGJ1n/vAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAABSSURBVHgBXY2xDcAwCAQNk6ShYIxMFmeyjIEQRTIJMUhGskwD/D1PU9XfzK42S0SO0F6M+XP3XpCZcz8hHYj4hJa9E9GdBqjzHcKavULcs+vnAD0LMsH4zZo3AAAAAElFTkSuQmCC`
  const noDataImg = new Image()
  noDataImg.src = noDataImgSrc

  const baseSeriesData = isDataReady({ data })
    ? getDataSubset(data, [xVar, yVar]).filter(d => d[1] !== null && !Number.isNaN(d[1]))
    : []

    const absOptionsSeries = [
      {
        id: 'absence',
        type: 'line',
        data: baseSeriesData,
        symbol: 'circle',
        symbolSize: 8,
        itemStyle: {
          color: '#60A0DC',
          borderColor: '#0071DB',
          borderWidth: 2
        },
        lineStyle: {
          color: '#A1C4E4',
        },
      }
    ];

  const beforeCovidData = baseSeriesData.filter(d => d[0] <= xExtentsMissing[0])
  const duringCovidData = baseSeriesData.filter(d => d[0] == xExtentsMissing[0] || d[0] == xExtentsMissing[1])
  const afterCovidData = baseSeriesData.filter(d => d[0] >= xExtentsMissing[1])

  const gradeOptionsSeries = [
    {
      id: 'before-covid',
      type: 'line',
      data: beforeCovidData,
      symbol: 'circle',
      symbolSize: 8,
      itemStyle: {
        color: '#60A0DC',
        borderColor: '#0071DB',
        borderWidth: 2
      },
      lineStyle: {
        color: '#A1C4E4',
      },
    },
    {
      id: 'during-covid',
      type: 'line',
      data: duringCovidData,
      symbol: 'circle',
      symbolSize: 0,
      lineStyle: {
        color: '#A1C4E4',
        type: 'dotted'
      },
      markArea: {
        silent: true,
        itemStyle: {
          opacity: 0.4,
          color: {
            image: noDataImg,
            repeat: 'repeat',
          },
        },
        label: {
          color: '#757575',
          position: 'bottom',
          distance: 35
        },
        data: [
          [
            // TODO: Pass this in from SedaTimeseries
            { xAxis: xExtentsMissing[0] + 0.5, name: 'No Data', },
            { xAxis: xExtentsMissing[1] - 0.5 }
          ]
        ]
      }
    },
    {
      id: 'after-covid',
      type: 'line',
      data: afterCovidData,
      symbol: 'circle',
      symbolSize: 8,
      itemStyle: {
        color: '#60A0DC',
        borderColor: '#0071DB',
        borderWidth: 2
      },
      lineStyle: {
        color: '#A1C4E4',
      },
    },
  ]
  const options = {
    ...base,
    // series: metricGroup === 'abs' ? absOptionsSeries : gradeOptionsSeries
    series: gradeOptionsSeries
  }
  return extendTimeseriesStyle(options)
}

/**
 * Returns an eCharts options object
 * @param {*} variant
 * @param {*} param1
 * @returns
 */
export const getTimeseriesOptions = (context = {}) => {
  if (!context.data || context.data.length === 0) return {}
  const { xVar, yVar, extents, metricGroup, theme } = context
  const baseOptions = {
    grid: grid(),
    xAxis: xAxis({
      // varName: xVar,
      varName: metricGroup,
      extent: extents[0],
      zLevel: 10,
    }),
    yAxis: yAxis({
      // varName: yVar,
      varName: metricGroup,
      extent: extents[1],
      zLevel: 10
    }),
    tooltip: {
      show: true,
      trigger: 'item',
      confine: true,
      className: 'timeseries__tooltip',
      padding: 10,
      backgroundColor: theme.app.darkBackground,
      textStyle: {
        color: theme.palette.common.white,
        fontFamily: 'inherit',
      },
      extraCssText: `border-radius: ${theme.shape.borderRadius}px`,
      formatter: params => {
        return `
          <div class="timeseries__tooltip--content">
            <div class="timeseries__tooltip--year">
              ${params.data[0]}
            </div>
            <div class="timeseries__tooltip--value">
              <div>${getLang(`LABEL_SINGULAR_${metricGroup}`)}</div>
              <div>${formatYValue(metricGroup, params.data[1])}</div>
            </div>
            <div class="timeseries__tooltip--unit">
              ${getLang('HINT_TIMESERIES')}
            </div>
          </div>
        `
      }
    },
  }
  const overlayOptions = getOverlayOptions(
    context,
    baseOptions
  )
  return overlayOptions
}
